import React, { useState, useEffect, useRef } from 'react';
import './ChronoPulse.css';

// Components
import TopBar from "../../Components/Games/TopBar";
import overlay from "../../assets/img/overlay.png";
import chronoPulseIcon from "../../assets/img/icon-chronopluse.png";
import ResultPage from "../../Components/Games/ResultPage";
import GameIntroScreen from "../../Components/Games/GameIntroScreen";
import StartGame from "../../Components/Games/StartGame";
import { getUrlParams } from '../../apiService/timespam';
import { lifestyleApi } from '../../apiService/api';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";

const SESSION_LIMIT = 60;
const TARGETS = [5, 8];
const SUCCESS_MARGIN = 0.4;

const FEEDBACK_PHRASES = [
  "HUMAN METRONOME!", "CLOCKWORK PRECISION!", "NEURAL SYNC!",
  "TIME BENDER!", "INTERNAL RHYTHM!", "ON THE PULSE!", "ELITE TIMING!"
];

const chronoPulseTutorialSteps = [
  {
    icon: "⏱️",
    title: "TARGET TIME",
    desc: "Each round shows a target time like 5s or 8s."
  },
  {
    icon: "🧠",
    title: "COUNT",
    desc: "Count the seconds in your mind after the round begins."
  },
  {
    icon: "👇",
    title: "TAP",
    desc: "Tap exactly when you think the target time has passed."
  },
  {
    icon: "🎯",
    title: "SYNC",
    desc: "The closer your timing is, the higher your score and streak."
  }
];

const ChronoPulse2 = () => {
  useGameBackNavigation();
  // --- APP NAVIGATION FLOW ---
  const [phase, setPhase] = useState('INTRO');

  // --- GAME STATE ---
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [sessionTime, setSessionTime] = useState(SESSION_LIMIT);
  const [trialState, setTrialState] = useState('WAITING');
  const [target, setTarget] = useState(5);
  const [lastElapsed, setLastElapsed] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [goqiiBest, setGoqiiBest] = useState(0);

  // --- STATS FOR RESULT PAGE ---
  const [correctHits, setCorrectHits] = useState(0);
  const [wrongHits, setWrongHits] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [cid, setCid] = useState("");
  const [groupName, setGroupName] = useState("");
  const [gameStateApi, setGameStateApi] = useState("initial");
  const [lastInsertedId, setLastInsertedId] = useState(null);
  const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
  const [backendSkill, setBackendSkill] = useState("");
  const [weightages, setWeightages] = useState({ attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1, fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0, correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1 });
  const [configError, setConfigError] = useState(false);
  const [fastResponses, setFastResponses] = useState(0);

  const [tutorialStep, setTutorialStep] = useState(0);


  const startTimeRef = useRef(0);
  const timerRef = useRef(null);
  const lastInsertedIdRef = useRef(null);

  const startActualGame = async () => {
    const { encryptedId, gid } = getUrlParams();

    try {
      const res = await lifestyleApi.initialGameScore(encryptedId, gid);

      if (res.data?.code === 200) {
        const data = res.data.data;

        setCid(data.cid);
        setGroupName(data.groupName);
        setLastInsertedId(data.lastInsertedId);          // UI
        lastInsertedIdRef.current = data.lastInsertedId;
        setGameStateApi("initial");
      }
    } catch (err) {
      console.error("initialGameScore failed", err);
    }
    setScore(0);
    setCorrectHits(0);
    setWrongHits(0);
    setCurrentStreak(0);
    setMaxStreak(0);
    setFastResponses(0);
    setSessionTime(SESSION_LIMIT);
    setPhase('GAME');
    beginTrial();

    timerRef.current = setInterval(() => {
      setSessionTime(prev => {
        if (prev <= 1) {
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const fetchGameConfig = () => {
    setConfigError(false);
    const { encryptedId, gid } = getUrlParams();

    lifestyleApi.fetchGameDetails(encryptedId, gid)
      .then(res => {
        if (res.data?.code === 200) {
          const d = res.data.data;
          setBestScore(d.bestScore);
          setGoqiiBest(d.goqiiBest);
          setWeightages({
            attemptWeightage: d.attemptWeightage ?? 1,
            accuracyWeightage: d.accuracyWeightage ?? 1,
            streakWeightage: d.streakWeightage ?? 1,
            fastWeightage: d.fastWeightage ?? 1,
            scoreWeightage: d.scoreWeightage ?? 1,
            baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
            correctMinQuestion: d.correctMinQuestion || 1,
            streakMinQuestion: d.streakMinQuestion || 1,
            fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
            scorePoints: d.scorePoints || 1,
          });
        } else {
          setConfigError(true);
        }
      })
      .catch(err => {
        console.error("fetchGameDetails failed", err);
        setConfigError(true);
      });
  };

  useEffect(() => {
    fetchGameConfig();
  }, []);

  const beginTrial = () => {
    setTarget(TARGETS[Math.floor(Math.random() * TARGETS.length)]);
    setTrialState('ACTIVE');
    setFeedback("SENSE THE BEAT...");
    startTimeRef.current = performance.now();
  };

  const handlePulse = (e) => {
    if (phase !== 'GAME' || trialState !== 'ACTIVE') return;

    const now = performance.now();
    const elapsed = (now - startTimeRef.current) / 1000;
    const diff = Math.abs(elapsed - target);

    setLastElapsed(elapsed.toFixed(2));

    if (diff <= SUCCESS_MARGIN) {
      let fastBonus = 0;
      if (diff <= SUCCESS_MARGIN / 2) {
        setFastResponses(prev => prev + 1);
        fastBonus = weightages.fastWeightage * 10;
      }
      // currentStreak here is the streak count BEFORE this hit (n-1 for this attempt "n")
      const streakBonus = weightages.streakWeightage * 10 * currentStreak;
      const gained = weightages.scorePoints + streakBonus + fastBonus;
      setScore(s => Math.max(0, s + gained));
      setCorrectHits(prev => prev + 1);
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      setMaxStreak(prev => Math.max(prev, newStreak));
      setFeedback(FEEDBACK_PHRASES[Math.floor(Math.random() * FEEDBACK_PHRASES.length)]);
    } else {
      setWrongHits(prev => prev + 1);
      setCurrentStreak(0);
      setScore(s => Math.max(0, s - weightages.scorePoints / 2));
      setFeedback("PULSE MISSED!");
    }

    setTrialState('TRIAL_RESULT');

    setTimeout(() => {
      setSessionTime(currentTotal => {
        if (currentTotal > 0) beginTrial();
        return currentTotal;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);


  const saveFinalGameScore = async () => {
    const { encryptedId, gid } = getUrlParams();

    const goqiiUserId = encryptedId;
    const totalQuestions = correctHits + wrongHits;

    const payload = {
      goqiiUserId,
      gid,
      totalQuestions,
      correctAnswers: correctHits,
      wrongAnswers: wrongHits,
      longestStreak: maxStreak,
      fastResponse: fastResponses,
      score,
      cid,
      lastInsertedId: lastInsertedIdRef.current,
    };

    try {
      const res = await lifestyleApi.saveGameScore(
        payload.goqiiUserId,
        payload.gid,
        payload.totalQuestions,
        payload.correctAnswers,
        payload.wrongAnswers,
        payload.longestStreak,
        payload.fastResponse,
        payload.score,
        payload.cid,
        payload.lastInsertedId
      );

      if (res?.data?.data?.cognitiveScore !== undefined) {
        setBackendCognitiveScore(res.data.data.cognitiveScore);
      }

      if (res?.data?.data?.skill) {
        setBackendSkill(res.data.data.skill);
      }

    } catch (err) {
      console.error("saveGameScore failed", err);
    }
  };
  const endGame = () => {
    clearInterval(timerRef.current);
    setBestScore(prev => Math.max(prev, score));
    saveFinalGameScore();   //  CALL API HERE
    setPhase('RESULT');
  };



  const getQuote = () => {
    const total = correctHits + wrongHits;
    const accuracy = total > 0 ? Math.round((correctHits / total) * 100) : 0;

    if (backendCognitiveScore >= 90 || (accuracy >= 90 && maxStreak >= 8))
      return "Human metronome! Your inner clock is incredibly precise.";
    if (backendCognitiveScore >= 75 || (accuracy >= 80 && maxStreak >= 5))
      return "Great timing! Your brain senses rhythm very well.";
    if (backendCognitiveScore >= 60 || accuracy >= 65)
      return "Good pulse control. Your timing skills are improving.";
    if (backendCognitiveScore >= 40)
      return "Nice effort. Train more to sharpen your inner clock.";
    return "Every tap trains your sense of time. Stay in sync and try again!";
  };

  return (
    <div
      className={`chrono-pulse-root ${sessionTime < 10 && phase === 'GAME' ? 'chrono-pulse-critical' : ''}`}
      onClick={handlePulse}
    >
      {configError && (
        <div className="api-error-overlay">
          <div className="api-error-card">
            <div className="api-error-title">Something went wrong</div>
            <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
            <button className="api-error-retry-btn" onClick={(e) => { e.stopPropagation(); fetchGameConfig(); }}>Retry</button>
          </div>
        </div>
      )}

      {/* TOP BAR - Separated from center logic */}
      {phase === 'GAME' && (
        <TopBar score={score} timeLeft={sessionTime} />
      )}

      <div className="chrono-pulse-main-container">
        {/* 1️⃣ Phase: Intro */}
        {phase === 'INTRO' && (
          <GameIntroScreen
            backgroundImage={overlay}
            gameLogo={chronoPulseIcon}
            title="Chrono Pulse"
            onStart={() => setPhase('BENEFITS')}
          />
        )}

        {/* 2️⃣ Phase: Benefits */}
        {phase === 'BENEFITS' && (
          <StartGame
            backgroundImage={overlay}
            hbHeading="Reaction Time"
            hbDescription="Mastering internal timing enhances focus, coordination, and neural processing speed."
            yourBest={bestScore}
            goqiiBest={goqiiBest}
            onStart={() => setPhase('TUTORIAL')}
          />
        )}

        {/* 3️⃣ Phase: Tutorial */}
       {phase === 'TUTORIAL' && (
  <div className="chronopulse chronopulse-app-container">

    <div className="chronopulse-tutorial-frame">

      {/* Header */}
      <div className="chronopulse-tutorial-header">

        <div className="chronopulse-tutorial-label">
          TUTORIAL
        </div>

        <button
          className="chronopulse-tutorial-skip"
          onClick={() => {
            setPhase('STARTING');
            setTimeout(() => startActualGame(), 1200);
          }}
        >
          Skip ▶
        </button>

      </div>

      {/* Step Instruction */}
      <div className="chronopulse-tutorial-instruction">
        Step {tutorialStep + 1}: {chronoPulseTutorialSteps[tutorialStep].title}
      </div>

      {/* Icon */}
      <div className="chronopulse-tutorial-icon">
        {chronoPulseTutorialSteps[tutorialStep].icon}
      </div>

      {/* Description */}
      <p className="chronopulse-tutorial-desc">
        {chronoPulseTutorialSteps[tutorialStep].desc}
      </p>

      {/* Buttons */}
      {tutorialStep < chronoPulseTutorialSteps.length - 1 ? (
        <button
          className="chronopulse-tutorial-btn"
          onClick={() => setTutorialStep(tutorialStep + 1)}
        >
          Next
        </button>
      ) : (
        <button
          className="chronopulse-tutorial-btn"
          onClick={() => {
            setPhase('STARTING');
            setTimeout(() => startActualGame(), 1200);
          }}
        >
          Start Game
        </button>
      )}

    </div>

  </div>
)}

{phase === 'STARTING' && (
  <div className="chronopulse-start-overlay">
    <div className="chronopulse-start-text">START!</div>
  </div>
)}

        {/* 4️⃣ Phase: The Game */}
        {phase === 'GAME' && (
          <div className="chrono-pulse-viewport">
            {trialState === 'ACTIVE' && (
              <div className="chrono-pulse-action-zone">
                <div className="chrono-pulse-target-tag">TARGET: {target}.00s</div>
                <div className="chrono-pulse-scanner"></div>
                <div className="chrono-pulse-feedback">{feedback}</div>
              </div>
            )}

            {trialState === 'TRIAL_RESULT' && (
              <div className="chrono-pulse-result-zone">
                <div className="chrono-pulse-feedback" style={{ color: '#fff' }}>{feedback}</div>
                <div className="chrono-pulse-big-digit">{lastElapsed}s</div>
                <div className="chrono-pulse-subtext">OFFSET: {(lastElapsed - target).toFixed(2)}s</div>
              </div>
            )}
          </div>
        )}

        {/* 5️⃣ Phase: Result */}
        {phase === 'RESULT' && (
          <ResultPage
            visible={true}
            score={score}
            best={bestScore}
            correct={correctHits}
            wrong={wrongHits}
            longestStreak={maxStreak}
            fastResponses={fastResponses}
            quote={getQuote()}
            cognitiveScore={backendCognitiveScore}
            domainLabel={backendSkill}
            onReplay={() => setPhase('STARTING')}
          />
        )}
      </div>
    </div>
  );
};

export default ChronoPulse2;